// Holds the fms-agent JWT in heap memory only. Never localStorage or
// sessionStorage: an XSS that can read storage can also read memory, but
// storage persists across sessions and exfiltrates cheaper. A page reload
// simply mints again.

export class TokenMintError extends Error {
    status: number;

    constructor(status: number) {
        super(
            status === 429
                ? "You're sending requests too quickly, try again in a minute."
                : status === 403
                    ? "The chat beta is invite-only. Ask in #eng if you'd like access."
                    : "Chat is unavailable right now, try again later."
        );
        this.status = status;
    }
}

interface MintedToken {
    token: string;
    expiresAt: number;
}

// Class + DI + singleton (see CLAUDE.md "Front-end Architecture Standards").
// Deps are injected with sensible defaults so a test constructs a fresh
// instance with a mocked fetch — state lives in private fields, so there is
// no module-level mutable state and no need for jest.resetModules.
export class LangGraphTokenManager {
    private cached: MintedToken | null = null;
    private inflight: Promise<MintedToken> | null = null;

    constructor(
        // Wrapper, not a bare `fetch` reference: calling `this.fetchFn(...)`
        // on a stored native `fetch` invokes it with `this` = this instance,
        // which the browser rejects ("Illegal invocation"). The arrow calls
        // bare `fetch`, preserving the global receiver.
        private readonly fetchFn: typeof fetch = (...args) => fetch(...args),
        private readonly tokenEndpoint: string = process.env.REACT_APP_SERVER_URI + "/langgraph/token",
        private readonly refreshMarginMs: number = 60 * 1000
    ) {}

    // Returns a token at least 60s from expiry, minting if needed. Concurrent
    // callers share one in-flight mint (single-flight) so a burst of API calls
    // costs one mint, not one each.
    public async getToken(): Promise<string> {
        if (this.cached !== null && this.cached.expiresAt - Date.now() > this.refreshMarginMs) {
            return this.cached.token;
        }
        if (this.inflight === null) {
            this.inflight = this.mint().finally(() => {
                this.inflight = null;
            });
        }
        this.cached = await this.inflight;
        return this.cached.token;
    }

    // Called when the deployment rejects the token (401): drop it so the next
    // getToken() mints fresh. The caller retries exactly once.
    public invalidate(): void {
        this.cached = null;
    }

    private async mint(): Promise<MintedToken> {
        const response = await this.fetchFn(this.tokenEndpoint, {
            method: "POST",
            credentials: "include"
        });
        if (!response.ok) {
            throw new TokenMintError(response.status);
        }
        const body = await response.json();
        // Guard against a proxy-intercepted 200 with a non-token body: caching
        // "Bearer undefined" would silently break all subsequent requests.
        if (typeof body?.token !== "string" || typeof body?.expiresAt !== "number") {
            throw new TokenMintError(502);
        }
        return body as MintedToken;
    }
}

export const langGraphTokenManager = new LangGraphTokenManager();
